/**
 * formUndoStack — pure undo/redo for user input on a loaded document (SYD-2297,
 * SYD-2447, SYD-2314). Surfaces owning a Firestore write path register an
 * `UndoTarget`, call sites `record` the user's edits, and `useFormUndoKeybindings`
 * runs `undo`/`redo` and owns the lifetime: it clears on both edges of the surface it
 * is mounted on and whenever that surface changes the document it edits — a form
 * version or an entity record — so an entry can never be popped against a document it
 * was not recorded on. A surface holding one document inside another,
 * `EmbeddedEntityUI`, ends that document's entries alone by passing its path to
 * `clear`.
 *
 * Both stacks are plain arrays outside Valtio — nothing renders from them and their
 * values go back to Firestore, where a deep-proxied `Timestamp` would reach the
 * serializer as a `Proxy`, so `record` keeps `cloneDeep` copies. `state` is readonly,
 * and `availability` is the one proxy here, carrying just what the buttons render.
 *
 * An entry's payload is uniform, because `kind` picks the adapter method once for the
 * whole entry. A single user action that both writes fields and destroys or creates
 * documents therefore cannot be one entry. `pasteDownColumn` under
 * `overflow: 'createRows'` is such an action: only its field writes reach `record`,
 * so the rows it creates are outside the history these stacks describe.
 */

import { proxy } from 'valtio';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

export type UndoDirection = 'undo' | 'redo';

export type UndoChange = {
    docId: string;
    /** Firestore dot-path within the doc, e.g. `cells.col1.value` or `fields.f1.value`. */
    path: string;
    prev: unknown;
    next: unknown;
};

/** A document the action created, named so undo can remove it and redo can put it back under the same id. */
export type UndoDocument = { docId: string; data: Record<string, unknown> };

/** What a caller hands `recordOperation`: the server's receipt, plus how to name it on a button. */
export type UndoOperation = {
    operationId: string;
    count: number;
    /** The action in the user's words, as a noun phrase, so a button can render `Undo ${label}`. */
    label: string;
};

type UndoEntryBase = {
    id: string;
    /** Registry key of the surface owning the write path — a rows collection path, or a document path. */
    target: string;
    t: number;
};

export type UndoEntry =
    /** Field writes inside live documents, guarded value by value against the current one. */
    | (UndoEntryBase & { kind: 'fields'; changes: UndoChange[] })
    /**
     * A server-side operation the user reverses by id. The rows it destroyed are named
     * by a trash record rather than by anything the client holds — under the `allRows`
     * and `bundleId` scopes the client never saw them — so no payload reaches the stack.
     *
     * Both directions name a target state, not a delta: undo means every row of the
     * operation is live, redo means every row is trashed. Neither depends on where a
     * previous run stopped, so pressing again after a half-committed run finishes the
     * job, and pressing on a finished one is a no-op reporting nothing applied.
     */
    | (UndoEntryBase & { kind: 'operation' } & UndoOperation)
    /**
     * Documents one client action created, carried whole rather than by path: redo
     * must rewrite each row under the id the entry names, and `add` mints its own.
     *
     * Both directions name a target state, not a delta: undo means none of these
     * documents exist, redo means all of them do. A document already in the target
     * state is skipped rather than treated as a conflict, so an entry that only
     * partly applied is still correct from wherever the documents now stand.
     */
    | (UndoEntryBase & { kind: 'documents'; docs: UndoDocument[] });

/**
 * Adapter a mounted surface registers so the stack can read current values and write
 * reverted ones.
 *
 * Every verb below `apply` reports `targetStateHolds` beside its counts, and that flag
 * alone decides which stack `step` leaves the entry on. The counts cannot decide it:
 * `applied: 0` arrives from two opposite situations. Either every row was already in
 * the state the entry names, so the entry is satisfied and belongs on the other stack —
 * leaving it put makes the chord a dead key until a hundred later entries push it off
 * the end — or every row was refused, so the state does not hold and the entry must
 * stay for a second press. A verb that skips only what is already right answers true
 * always; a verb that can refuse answers false when it did.
 *
 * `step` reads the answer only after the call resolves. A rejection is pushed back and
 * rethrown before the rule runs, so a failed write never reaches it.
 *
 * A verb that can lose its ability to reverse an operation reports that too, and the
 * stack discards the entry instead of pretending a second press might work. `found` is
 * that report: it counts what the verb still holds of the operation, and zero is not a
 * count of rows the way `applied` and `skipped` are — it says the operation itself is
 * gone. No direction can bring it back, so the entry goes nowhere and `step` answers
 * `expired` rather than `applied: 0`.
 *
 * A verb answers from server state, never from the surface's loaded documents. Those
 * documents are one page of a paginated query, and a row missing from a page is not a
 * row that is gone — a verb that reads the page skips every row the user has since
 * scrolled past, then reports a target state that does not hold, and the entry moves to
 * a stack that can no longer reach it. The round trip is what the answer costs.
 */
export type UndoTarget = {
    read: (docId: string, path: string) => unknown;
    apply: (docId: string, writes: { path: string; value: unknown }[]) => Promise<void>;
    /**
     * Moves a whole operation to the direction's target state and reports what it
     * found. Answers `targetStateHolds` false when a redo refused a row — a row a
     * colleague locked while it was back on screen, or a Form Derive projection's row.
     * Undo refuses nothing, so it always answers true.
     *
     * `found` counts the records of the operation the verb can still reach, and
     * `found: 0` means it can no longer reach the operation at all — past the
     * retention window that is permanent. It is the only signal separating that from
     * a finished job, because both report `applied: 0`.
     *
     * Surfaces owning no reversible operation leave the verb out, which makes an
     * `'operation'` entry on that target `unavailable`, the same status a missing
     * adapter yields.
     */
    stepOperation?: (
        operationId: string,
        direction: UndoDirection,
    ) => Promise<{ applied: number; skipped: number; found: number; targetStateHolds: boolean }>;
    /**
     * Moves every named document to the direction's target state — absent under
     * `'undo'`, present under `'redo'` — and reports what it moved. The verb owns
     * its own guard: a document already in that state is counted as skipped, never
     * as a conflict. It refuses nothing, so its `targetStateHolds` is always true.
     * Surfaces owning no document lifecycle leave it out, which makes a
     * `'documents'` entry on that target `unavailable`.
     */
    stepDocuments?: (
        docs: readonly UndoDocument[],
        direction: UndoDirection,
    ) => Promise<{ applied: number; skipped: number; targetStateHolds: boolean }>;
};

export type UndoResult =
    | { status: 'applied'; applied: number; skipped: number }
    | { status: 'empty' }
    | { status: 'unavailable' }
    | { status: 'conflict' }
    /** The operation is past recall: nothing holds it any more, in either direction. */
    | { status: 'expired' };

/** What consumers get through `formUndoStack.state`: the live stacks, no write path. */
export type FormUndoState = { readonly undo: readonly UndoEntry[]; readonly redo: readonly UndoEntry[] };

/** One direction's button: whether it can run, and what the top entry calls the action. */
export type UndoAvailability = {
    available: boolean;
    /** `undefined` on a `'fields'` entry, where the button reads plain "Undo" or "Redo". */
    label: string | undefined;
    /**
     * The id of the entry this direction's next press would act on, `undefined` when
     * the stack is empty. It is the only reactive way to ask whether a particular
     * recorded entry is still the one a press reaches: `state` holds unproxied arrays
     * and `available` does not move when one `'fields'` entry lands on another.
     * `FormRecoveryBanner` compares it against the id its restore recorded, so
     * "Undo restore" can never revert an edit made after the restore.
     */
    entryId: string | undefined;
};

/**
 * What the two buttons render. `available` means the stack holds an entry AND no step
 * is running: while one is in flight both directions read as unavailable, whatever the
 * stacks hold, so a double click cannot reverse a second operation the user never
 * asked about. `label` still names the top entry, so a disabled button keeps its words.
 */
export type FormUndoAvailability = Record<UndoDirection, UndoAvailability>;

const UNDO_GROUP_IDLE_MS = 900;

/**
 * The cap counts entries, not bytes, which stays honest only while an entry stays
 * small: a `'fields'` entry holds one edit's values and an `'operation'` entry holds
 * an id and a count, never the rows the operation touched. A `'documents'` entry is
 * the exception — it holds whole rows, so its size is the size of the paste that
 * made it and no small constant bounds it.
 */
const MAX_ENTRIES = 100;

const stacks: { undo: UndoEntry[]; redo: UndoEntry[] } = { undo: [], redo: [] };

/** The same two arrays, exposed under a type that cannot push, pop or splice them. */
const state: FormUndoState = stacks;

/**
 * The entries stay unproxied, so this mirrors only what the two buttons render.
 * Everything that moves either stack's top calls `syncAvailability`.
 */
const availability = proxy<FormUndoAvailability>({
    undo: { available: false, label: undefined, entryId: undefined },
    redo: { available: false, label: undefined, entryId: undefined },
});

/**
 * How many steps are awaiting their surface. Popping the entry before the await only
 * disables the direction whose *last* entry is in flight; with entries below it both
 * buttons stay live, and a second click pops the next one down and runs it beside the
 * first — one double click, two operations reversed, and nothing on screen saying why.
 * So availability reads this too, and both directions go dead until it is back to zero.
 *
 * A count rather than a boolean: two overlapping steps then re-enable the buttons when
 * the later one lands, not when the earlier one does.
 */
let stepsInFlight = 0;

/**
 * Raised by every `clear`, captured by every step before it awaits. A step that
 * comes back to a different generation was cleared while it was in flight and puts
 * its entry nowhere: the surface the entry was recorded on is gone, and re-adding it
 * would offer the next press over whatever document the surface now shows.
 *
 * A scoped clear raises it too, so it ends every step in flight rather than only one
 * whose entry it names. Losing a redo the clear did not name costs one press; keeping
 * an entry a clear did name is the bug this exists for.
 */
let clearGeneration = 0;

/** Whether any step is awaiting its surface, read at event time, never rendered. */
const stepping = (): boolean => stepsInFlight > 0;

/**
 * Assigns the six leaves rather than replacing either direction, so a button
 * subscribed to one direction does not re-render when the other one moves. It is the
 * only writer of the leaves — every path that moves a stack top or `stepsInFlight`
 * calls it rather than assigning.
 */
const syncAvailability = (): void => {
    const undoTop = stacks.undo.at(-1);
    availability.undo.available = stepsInFlight === 0 && undoTop !== undefined;
    availability.undo.label = undoTop?.kind === 'operation' ? undoTop.label : undefined;
    availability.undo.entryId = undoTop?.id;

    const redoTop = stacks.redo.at(-1);
    availability.redo.available = stepsInFlight === 0 && redoTop !== undefined;
    availability.redo.label = redoTop?.kind === 'operation' ? redoTop.label : undefined;
    availability.redo.entryId = redoTop?.id;
};

/**
 * Registrations per target key, oldest first. A key can be claimed by more than
 * one live surface — the same rows collection rendered twice, or a `fieldId`
 * under one page scope — so this is a stack, not a slot: the last registration
 * is the adapter in force, and the key survives until the last unregister.
 */
const adapters = new Map<string, UndoTarget[]>();

let entrySequence = 0;

const nextEntryId = (): string => {
    entrySequence += 1;
    return `undo-${entrySequence}`;
};

/**
 * Refcounted registration, mirroring `entityRecordStore`'s keyed registry: N
 * mounts of one key survive until the Nth unregister, so a surface unmounting
 * cannot leave its twin's entries `unavailable`. The returned unregister drops
 * exactly its own registration and is idempotent.
 */
const registerTarget = (target: string, adapter: UndoTarget): (() => void) => {
    adapters.set(target, [...(adapters.get(target) ?? []), adapter]);
    let released = false;
    return () => {
        if (released) return;
        const registrations = adapters.get(target);
        if (registrations === undefined) return;
        const index = registrations.lastIndexOf(adapter);
        if (index === -1) return;
        released = true;
        const remaining = [...registrations.slice(0, index), ...registrations.slice(index + 1)];
        if (remaining.length === 0) adapters.delete(target);
        else adapters.set(target, remaining);
    };
};

/** The most recently registered live adapter for a key, or `undefined` if none is mounted. */
const adapterFor = (target: string): UndoTarget | undefined => adapters.get(target)?.at(-1);

const capFront = (entries: UndoEntry[]): void => {
    if (entries.length > MAX_ENTRIES) {
        entries.splice(0, entries.length - MAX_ENTRIES);
    }
};

/**
 * Appends the user's edit. Consecutive edits to the same target/doc/path within
 * `UNDO_GROUP_IDLE_MS` coalesce into one entry, so a typed word undoes as a word.
 * Only a `'fields'` entry coalesces — see `recordOperation`.
 *
 * @returns the id of the entry the edit ended up in — a new one, or the one it
 * coalesced into — and `null` when nothing was recorded, which is both the
 * every-change-is-a-no-op case and the edit that cancelled the entry above it out.
 * A caller that later has to ask "is my edit still what a press would reach"
 * compares this against `availability`; every other caller ignores it.
 */
const record = (target: string, changes: UndoChange[]): string | null => {
    const captured = changes.map((change) => ({
        ...change,
        prev: cloneDeep(change.prev),
        next: cloneDeep(change.next),
    }));
    const effective = captured.filter((change) => !isEqual(change.prev, change.next));
    if (effective.length === 0) return null;

    stacks.redo.length = 0;

    const top = stacks.undo.at(-1);
    const incoming = effective[0];
    const coalesces =
        top !== undefined &&
        top.kind === 'fields' &&
        top.target === target &&
        top.changes.length === 1 &&
        effective.length === 1 &&
        top.changes[0].docId === incoming.docId &&
        top.changes[0].path === incoming.path &&
        Date.now() - top.t <= UNDO_GROUP_IDLE_MS;

    const recordedId = ((): string | null => {
        if (coalesces) {
            if (isEqual(top.changes[0].prev, incoming.next)) {
                stacks.undo.pop();
                return null;
            }
            top.changes[0].next = incoming.next;
            top.t = Date.now();
            return top.id;
        }
        const entryId = nextEntryId();
        stacks.undo.push({ kind: 'fields', id: entryId, target, changes: effective, t: Date.now() });
        capFront(stacks.undo);
        return entryId;
    })();
    syncAvailability();
    return recordedId;
};

/**
 * Appends a server-side operation the user can reverse by id. It never coalesces:
 * two deletes are two operations with two ids, and merging them would leave one of
 * them with nothing naming its rows.
 */
const recordOperation = (target: string, operation: UndoOperation): void => {
    stacks.redo.length = 0;
    stacks.undo.push({ kind: 'operation', id: nextEntryId(), target, t: Date.now(), ...operation });
    capFront(stacks.undo);
    syncAvailability();
};

/**
 * Appends the documents one action created. It never coalesces: each entry names
 * its own documents, and merging two would leave one paste's rows reachable by no
 * `⌘Z` at all. The payloads are cloned for the reason `record` clones its values.
 */
const recordDocuments = (target: string, docs: UndoDocument[]): void => {
    stacks.redo.length = 0;
    stacks.undo.push({
        kind: 'documents',
        id: nextEntryId(),
        target,
        t: Date.now(),
        docs: docs.map((doc) => ({ docId: doc.docId, data: cloneDeep(doc.data) })),
    });
    capFront(stacks.undo);
    syncAvailability();
};

const groupWrites = (
    changes: UndoChange[],
    value: (change: UndoChange) => unknown,
): Map<string, { path: string; value: unknown }[]> =>
    changes.reduce((byDoc, change) => {
        const writes = byDoc.get(change.docId) ?? [];
        return byDoc.set(change.docId, [...writes, { path: change.path, value: value(change) }]);
    }, new Map<string, { path: string; value: unknown }[]>());

/**
 * Drops an entry that could not run. Its target being gone, or its value having
 * moved, is evidence the document left the history these stacks describe — the
 * same evidence that already empties `redo` on a forward edit — so the whole redo
 * chain goes with it and `⌘⇧Z` cannot reapply a value over data that has since
 * moved. `undo` is never emptied wholesale: its entries are guarded change by
 * change, so a stale one is discarded on its own turn and the rest stay usable.
 */
const discard = (from: UndoEntry[]): void => {
    from.pop();
    stacks.redo.length = 0;
    syncAvailability();
};

/**
 * Shared engine for `undo` and `redo`: the direction alone decides which stack is
 * popped, which side of a change the current value must still equal, and which side
 * is written back. An entry whose target has no live adapter is discarded, as is a
 * `'fields'` entry whose current value no longer matches what the entry wrote.
 *
 * `kind` is the dispatch seam. An `'operation'` entry is not guarded here at all:
 * the callable owns existence and refusal, and its `targetStateHolds` — never its
 * counts — decides which stack the entry lands on. `applied: 0` names two outcomes
 * the counts cannot tell apart. Every row already being in the state the entry asks
 * for satisfies the entry, which then belongs on the far stack; every row having
 * been refused, as a redo's delete refuses a row a colleague locked, leaves the
 * entry where it came from, so unlocking the row and pressing the chord again
 * finishes the job. Comparing payloads is ruled out either way, because a formula
 * trigger rewrites a restored row shortly after it lands and an equality guard
 * would fail on exactly the rows a restore just brought back. The status stays
 * `applied` in both cases, with the untouched rows reported as skipped.
 *
 * An operation the verb can no longer reach — `found: 0` — is discarded instead, and
 * reported as `expired`: a second press cannot help, so there is nothing to move the
 * entry to the other stack for. The redo chain goes with it for the reason `discard`
 * gives.
 *
 * Both kinds pop before they await and sync availability on the spot, so the entry in
 * flight is off its stack for the whole round trip and no press can reach it twice.
 * What keeps the entries *below* it out of reach is `step`, not this: it raises
 * `stepsInFlight` for the duration, which reads through to both buttons.
 *
 * A `'documents'` entry is dispatched the same way and reads the same flag, for the
 * same reason: its verb answers existence next to the write that existence protects.
 * It reports `targetStateHolds: true` always, because it refuses nothing.
 *
 * A `clear` landing mid-flight wins over both kinds: the generation is read before the
 * await and again after it, and a step that finds it moved leaves the stacks as the
 * clear left them. The verb still ran, so the caller is told what it did.
 */
const runStep = async (direction: UndoDirection): Promise<UndoResult> => {
    const [from, to] =
        direction === 'undo' ? [stacks.undo, stacks.redo] : [stacks.redo, stacks.undo];
    const generation = clearGeneration;

    const entry = from.at(-1);
    if (entry === undefined) return { status: 'empty' };

    const adapter = adapterFor(entry.target);
    if (adapter === undefined) {
        discard(from);
        return { status: 'unavailable' };
    }

    if (entry.kind === 'operation') {
        if (adapter.stepOperation === undefined) {
            discard(from);
            return { status: 'unavailable' };
        }
        from.pop();
        syncAvailability();
        const outcome = await adapter
            .stepOperation(entry.operationId, direction)
            .catch((reason: unknown) => {
                if (clearGeneration === generation) {
                    from.push(entry);
                    syncAvailability();
                }
                throw reason;
            });
        if (clearGeneration !== generation) {
            return outcome.found === 0
                ? { status: 'expired' }
                : { status: 'applied', applied: outcome.applied, skipped: outcome.skipped };
        }
        if (outcome.found === 0) {
            stacks.redo.length = 0;
            syncAvailability();
            return { status: 'expired' };
        }
        const landedOn = outcome.targetStateHolds ? to : from;
        landedOn.push(entry);
        capFront(landedOn);
        syncAvailability();
        return { status: 'applied', applied: outcome.applied, skipped: outcome.skipped };
    }

    if (entry.kind === 'documents') {
        if (adapter.stepDocuments === undefined) {
            discard(from);
            return { status: 'unavailable' };
        }
        from.pop();
        syncAvailability();
        const outcome = await adapter
            .stepDocuments(entry.docs, direction)
            .catch((reason: unknown) => {
                if (clearGeneration === generation) {
                    from.push(entry);
                    syncAvailability();
                }
                throw reason;
            });
        if (clearGeneration !== generation) {
            return { status: 'applied', applied: outcome.applied, skipped: outcome.skipped };
        }
        const landedOn = outcome.targetStateHolds ? to : from;
        landedOn.push(entry);
        capFront(landedOn);
        syncAvailability();
        return { status: 'applied', applied: outcome.applied, skipped: outcome.skipped };
    }

    const surviving = entry.changes.filter((change) =>
        isEqual(
            adapter.read(change.docId, change.path),
            direction === 'undo' ? change.next : change.prev,
        ),
    );
    if (surviving.length === 0) {
        discard(from);
        return { status: 'conflict' };
    }

    from.pop();
    syncAvailability();
    const writes = groupWrites(surviving, (change) =>
        direction === 'undo' ? change.prev : change.next,
    );
    const outcomes = await Promise.allSettled(
        [...writes].map(([docId, docWrites]) => adapter.apply(docId, docWrites)),
    );
    const failed = outcomes.filter(
        (outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected',
    );

    /**
     * A multi-doc entry is one write per doc, so a rejection can leave some rows
     * reverted and some not. Nothing landing means the step never happened and the
     * entry goes back where it came from, so the chord can simply be pressed again;
     * anything landing means the entry now describes the far side and it moves, and
     * the per-change guard skips the rows that never got there. Either way the
     * caller sees the rejection and routes it to the write-error handler.
     */
    if (clearGeneration === generation) {
        const landed = failed.length < outcomes.length ? to : from;
        landed.push(entry);
        capFront(landed);
        syncAvailability();
    }
    if (failed.length > 0) throw failed[0].reason;

    return {
        status: 'applied',
        applied: surviving.length,
        skipped: entry.changes.length - surviving.length,
    };
};

/**
 * Runs one step with both buttons held down for its duration. The count is raised
 * synchronously, before the first press has returned, so the second press of a double
 * click already finds the buttons disabled; it is lowered on the resolved and the
 * rejected path alike, so a verb that throws leaves the buttons live rather than dead.
 */
const step = (direction: UndoDirection): Promise<UndoResult> => {
    stepsInFlight += 1;
    syncAvailability();
    return runStep(direction).finally(() => {
        stepsInFlight -= 1;
        syncAvailability();
    });
};

const undo = (): Promise<UndoResult> => step('undo');

const redo = (): Promise<UndoResult> => step('redo');

/**
 * True when the key belongs to the scope: the key is the scoped document itself, or
 * sits under it at a path or field boundary. The boundary is what makes the match a
 * scope rather than a text prefix — without it, clearing `…/records/rec-1` would take
 * `…/records/rec-10` with it.
 */
const withinScope = (key: string, scope: string): boolean =>
    key === scope ||
    (key.startsWith(scope) && (key[scope.length] === '/' || key[scope.length] === '#'));

/**
 * Splices the scope's entries out in place, because the arrays are the ones `state`
 * exposes and the ones a step in flight holds a reference to. Order survives.
 */
const dropScope = (entries: UndoEntry[], scope: string): void => {
    const kept = entries.filter((entry) => !withinScope(entry.target, scope));
    if (kept.length !== entries.length) entries.splice(0, entries.length, ...kept);
};

/**
 * Ends recorded entries — every one, or one document's.
 *
 * @param scope a document path whose entries end, leaving every other entry in place
 * and in order; omitted, both stacks empty. Undo targets are keyed by the document
 * path they write: `ArTableBody` registers the rows collection path
 * (`${documentPath}/_art_${tableId}`) and `useFieldUI` registers
 * `${documentPath}#${fieldId}`. A document's path is therefore a prefix of every key
 * belonging to it and of no other, and the whole correctness of a scoped clear rests
 * on that.
 */
const clear = (scope?: string): void => {
    clearGeneration += 1;
    if (scope === undefined) {
        stacks.undo.length = 0;
        stacks.redo.length = 0;
    } else {
        dropScope(stacks.undo, scope);
        dropScope(stacks.redo, scope);
    }
    syncAvailability();
};

export const formUndoStack = Object.freeze({
    state,
    availability,
    registerTarget,
    record,
    recordOperation,
    recordDocuments,
    undo,
    redo,
    stepping,
    clear,
});
